<template>
  <FreePageLayout>
    <template #header>
      <FreeHeader class="p-4" is-centered>
        <template #leftAction>
          <FreeBackButton @click="handlePageLeave" />
        </template>
        <template #default>
          <h2
            class="text-text-neutral-primary text-center text-lg font-semibold"
          >
            {{ t('payment.title') }}
          </h2>
        </template>
      </FreeHeader>
    </template>
    <template #default>
      <FreeLoader v-if="isLoading" class="m-auto" />
      <template v-else>
        <div class="relative flex grow flex-col">
          <OrderNumber class="mb-4" :order-number="order.number" />
          <FlightDetails
            v-if="dict"
            class="-mx-4 mb-5"
            :passengers="passengers"
            :flights="mappedFlights"
            :dict="dict"
            :is-youth="isYouth"
            :count-passengers="passengers.length"
            :search-query="offerSearchQuery"
          />
          <CountdownTimer
            v-if="expiresAt"
            :expires-at="expiresAt"
            @onExpired="showExpiredErrorModal"
          >
            <p class="text-text-neutral-tertiary text-sm font-medium">
              {{
                t('payment.timer.title', {
                  orderType: t('payment.timer.order'),
                })
              }}
            </p>
          </CountdownTimer>
          <BloomreachBanner
            class="mt-6"
            :placement="BannerPlacement.BILL_PAID"
          />
          <CharterFlightInfoContainer v-if="isFunSunOffer" class="mt-6" />
          <ApplyPromocode
            v-if="bill"
            :class="isFunSunOffer ? 'mt-4' : 'mt-6'"
            :order-id="orderId"
            :bill-id="bill.id"
            :flights="mappedFlights"
            :bookings="bookingProducts"
            @onAutomaticApply="showAutoAppliedPromocodeAlert"
          />
          <ApplyCashback
            class="mt-2"
            :order-id="orderId"
            @onError="showLoyaltyCashbackAlert"
          />
          <OrderPricing
            v-if="bill"
            class="mt-6"
            :order-id="orderId"
            :bill-id="bill.id"
            @onPriceChange="openPriceChangeAlert"
          />
        </div>
        <OrderPayButton
          v-if="bill"
          class="sticky bottom-4 mt-16 py-4"
          :order-id="orderId"
          :bill-id="bill.id"
          @onPayClick="pay"
        />
      </template>
      <div v-if="isCabinetLoading" class="fixed left-0 top-0 z-50">
        <FullScreenLoader />
      </div>
      <DefaultPaymentErrorModal
        :name="defaultErrorModal"
        :from-cabinet="Boolean(fromCabinet)"
        @onProceed="goToMainPage"
        @onRetry="router.go(0)"
      />
      <PaidPaymentErrorModal
        :name="paidErrorModal"
        @onProceed="goToOrderDetails"
      />
      <ExpiredPaymentModal
        :name="expiredErrorModal"
        @onProceed="handleExpiredModalProceed"
      />
      <PaymentErrorModal
        :name="paymentErrorModal"
        @onProceed="hidePaymentErrorModal"
      />
      <ConfirmationModal
        :modal-name="pageLeaveModal"
        @onAgree="agreeAndLeavePage"
      />
      <Teleport to="body">
        <div class="fixed w-full">
          <FreeAlertWithUseAlert
            :use-alert="createAlertInstance"
            :icons="{
              info: InfoIcon,
              warning: RedWarningIcon,
            }"
            class="bottom-28 left-0 right-0 mx-4"
          >
            <template #default="{ content }">
              <span class="text-text-neutral-primary">
                {{ content.message }}
              </span>
            </template>
          </FreeAlertWithUseAlert>
        </div>
      </Teleport>
    </template>
  </FreePageLayout>
</template>

<script setup lang="ts">
  import {
    onMounted,
    ref,
    toRef,
    unref,
    watch,
    watchEffect,
    computed,
  } from 'vue';
  import { toSafeInteger } from 'lodash-es';
  import { useRouter, onBeforeRouteLeave, useRoute } from 'vue-router';
  import { useI18n } from 'vue-i18n';
  import {
    FreeBackButton,
    FreeHeader,
    FreeLoader,
    FreePageLayout,
    ConfirmationModal,
    CountdownTimer,
    appNavLinks,
    PaymentErrorModal,
    ExpiredPaymentModal,
    PaidPaymentErrorModal,
    DefaultPaymentErrorModal,
    FullScreenLoader,
    FlowType,
    CharterFlightInfoContainer,
    FreeAlertWithUseAlert,
  } from 'freedom-shared';
  import { controllerModel, hashCodeLib, providerClassLib } from 'shared';
  import { prettyPrice } from '@aviata/text-fns';
  import { hasPlatformError } from '@aviata/platform-fns';
  import type { Payments } from '@aviata/platform-types';

  import { usePayment } from '@/features/payment';
  import { OrderPricing } from '@/features/order-pricing';
  import { ApplyPromocode } from '@/features/apply-promocode';
  import { ApplyCashback } from '@/features/apply-cashback';
  import { OrderPayButton } from '@/features/order-pay-button';

  import { BloomreachBanner } from '@/entities/bloomreach-banner';

  import {
    analyticsEventsLib,
    BannerPlacement,
    buildCombinedSearchQueryFromOrderProducts,
  } from '@/shared/lib';
  import { sentryModel } from '@/shared/model';
  import { useAlert } from '@/shared/ui';

  import InfoIcon from '@/assets/images/info-icon.svg?component';
  import RedWarningIcon from '@/assets/images/red-warning.svg?component';

  import { FlightDetails, OrderNumber } from './ui';

  const props = defineProps<{
    orderId: string;
    fromCabinet?: string;
  }>();

  const router = useRouter();
  const route = useRoute();
  const { t } = useI18n();

  const createAlertInstance = () => useAlert('payment-page');
  const alert = createAlertInstance();

  const {
    order,
    bill,
    dict,
    error,
    expiresAt,
    passengers,
    mappedFlights,
    bookingProducts,
    isLoading,
    isError,
    isBillPaid,
    isBillExpired,
    isOrderIssued,
    startPayment,
  } = usePayment({
    orderId: toRef(props, 'orderId'),
  });

  const isPageLeaveConfirmed = ref(false);
  const isCabinetLoading = ref(false);

  const modal = controllerModel.useModal();
  const paidErrorModal = 'paid-payment-modal';
  const paymentErrorModal = 'payment-error-modal';
  const defaultErrorModal = 'default-error-modal';
  const expiredErrorModal = 'expired-payment-modal';
  const pageLeaveModal = 'payment-page-leave-warning-modal';
  const showExpiredErrorModal = () => modal.show(expiredErrorModal);
  const showDefaultErrorModal = () => modal.show(defaultErrorModal);
  const showPaidErrorModal = () => modal.show(paidErrorModal);
  const showPageLeaveWarning = () =>
    modal.show(pageLeaveModal, {
      title: t('payment.leaveWarning.title'),
      message: t('payment.leaveWarning.message'),
    });
  const showPaymentErrorModal = () => modal.show(paymentErrorModal);
  const hidePaymentErrorModal = () => modal.hide(paymentErrorModal);

  const searchParams = computed(() => {
    const searchQuery =
      order.value?.products?.[0]?.data?.offer?.$meta?.search_query;
    if (!searchQuery) {
      return { isYouth: false };
    }
    return hashCodeLib.getSearchParams(searchQuery as string);
  });

  const { isYouth } = searchParams.value;

  const isFunSunOffer = computed(() =>
    order.value?.products.some(product =>
      providerClassLib.isFunSun(product.data?.offer?.$meta?.provider_class),
    ),
  );

  const offerSearchQuery = computed(() =>
    buildCombinedSearchQueryFromOrderProducts(order.value?.products),
  );

  watchEffect(() => {
    if (bill.value) {
      if (
        toSafeInteger(bill.value?.min_amount) ===
        toSafeInteger(bill.value?.amount)
      ) {
        alert.show({
          type: 'info',
          message: t('payment.min-price', {
            amount: bill.value?.min_amount,
          }),
          duration: 3000,
        });
      }
    }
  });

  const showAutoAppliedPromocodeAlert = () => {
    alert.show({
      type: 'info',
      message: t('payment.autoAppliedPromocode'),
      duration: 5000,
    });
  };

  const openPriceChangeAlert = (newPrice: string) => {
    alert.show({
      type: 'warning',
      message: t('booking.priceChange', {
        price: prettyPrice(newPrice),
      }),
      duration: 7000,
    });
  };

  const showLoyaltyCashbackAlert = (message: string) => {
    alert.show({
      type: 'warning',
      message,
      duration: 5000,
    });
  };

  const goToMainPage = () =>
    router.replace({
      name: 'MainPage',
      query: { source: 'payment_page', sourceElement: 'error_modal' },
    });

  const goToOrderDetails = () =>
    appNavLinks.openCabinet('orders', props.orderId);

  const handlePageLeave = () => {
    if (props.fromCabinet) {
      appNavLinks.openCabinet(FlowType.AVIA);
    } else {
      goToMainPage();
    }
  };

  /**
   * @description При попытке покинуть страницу юзеру показываем модалку с предупреждением, только после подтверждение переходим на новую страницу.
   * Единственный случай когда предупреждение не показывается - это редайрект после успешной оплаты.
   * */
  function onPageLeaveAgree() {
    isPageLeaveConfirmed.value = true;
  }

  const handleExpiredModalProceed = () => {
    onPageLeaveAgree();
    goToMainPage();
  };

  const agreeAndLeavePage = () => {
    onPageLeaveAgree();
    handlePageLeave();
  };

  const pay = () => {
    // Send analytics event when button is clicked, before payment attempt
    // This ensures we track all button clicks, even if payment fails
    if (order.value && bill.value) {
      const promocodeModifier = bill.value.dynamic_modifiers.find(
        modifier =>
          (modifier.modifier_type as  // TODO: Add to aviatakz libs
            | Payments.Bill.DynamicModifier['modifier_type']
            | 'PROMO_CODE') === 'PROMO_CODE',
      );

      analyticsEventsLib.payButtonClick({
        orderId: order.value.id,
        order: order.value,
        bill: bill.value,
        promocodeModifier,
        // @ts-expect-error TODO: Add to aviatakz libs
        promocodeNumber: promocodeModifier?.value ?? null,
        isCharter: isFunSunOffer.value,
      });
    }

    startPayment()
      .then(postPaymentAction => {
        if (postPaymentAction === 'goToMainPage') {
          agreeAndLeavePage();
        } else {
          isCabinetLoading.value = true;
          goToOrderDetails();
        }
      })
      .catch(error => {
        showPaymentErrorModal();
        sentryModel.captureException(
          sentryModel.getReadableError('ERR_FAILED_TO_PAY', error),
          { level: sentryModel.level.FATAL },
        );
      });
  };

  watch(isError, () => {
    const e = unref(error.value);

    if (isBillExpired.value) {
      showExpiredErrorModal();
      sentryModel.addBreadcrumb({
        level: sentryModel.level.INFO,
        data: {
          title: 'INFO_ORDER_EXPIRED',
          error: e,
        },
      });
      return;
    }

    if (isOrderIssued.value || isBillPaid.value) {
      showPaidErrorModal();
      sentryModel.addBreadcrumb({
        level: sentryModel.level.INFO,
        data: {
          title: 'INFO_ORDER_PAID',
          error: e,
        },
      });
      return;
    }

    if (hasPlatformError(e)) {
      showDefaultErrorModal();
      sentryModel.logBaseError({
        level: sentryModel.level.FATAL,
        error: e as unknown as Error,
        description: 'ERR_UNKNOWN_FAILED_TO_FETCH_ORDER_DATA',
        extra: {
          billId: bill?.value?.id,
          errors: e?.response?.data?.errors,
        },
      });
    }
  });

  onBeforeRouteLeave(() => {
    if (isPageLeaveConfirmed.value) {
      return true;
    }
    showPageLeaveWarning();
    return false;
  });

  onMounted(() => {
    const source = props.fromCabinet
      ? 'cabinet'
      : (route.query.source as string | null) || null;
    analyticsEventsLib.paymentPageOpen(source);

    sessionStorage.removeItem(
      analyticsEventsLib.config.sessionStorageAnalyticsKeys.analytics,
    );
    sessionStorage.removeItem(
      analyticsEventsLib.config.sessionStorageAnalyticsKeys.bloomreach,
    );
  });
</script>
